import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Button, Table, Input, Select } from 'antd'
import moment from 'moment'
import { getUploadedInvoices } from 'src/features/NewInvoicesSlice'
import { useDispatch, useSelector } from 'react-redux'
import { mapInvoiceToTableRow } from 'src/utils/invoiceUploadHelpers'

const { Search } = Input
const { Option } = Select

const SelectInvoicesModal = ({
  isOMSModalOpen,
  setIsOMSModalOpen,
  setSelectedInvoices,
  onInvoicesConfirmed,
  defaultSupplierTitle,
}) => {
  const [allInvoices, setAllInvoices] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [supplierFilter, setSupplierFilter] = useState('all')
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedRows, setSelectedRows] = useState([])
  const { uploadedInvoices, uploadedLoading } = useSelector(
    state => state.newInvoices,
  )
  const dispatch = useDispatch()
  const venueId = useSelector(state => state.venue?.venue?._id)

  useEffect(() => {
    if (isOMSModalOpen) {
      dispatch(getUploadedInvoices(venueId))
      setSearchQuery('')
      setSupplierFilter('all')
      setSelectedRowKeys([])
      setSelectedRows([])
    }
  }, [venueId, isOMSModalOpen, dispatch])

  useEffect(() => {
    if (!isOMSModalOpen || !defaultSupplierTitle || !allInvoices.length) {
      return
    }

    const hasMatchingSupplier = allInvoices.some(
      invoice => invoice.supplierTitle === defaultSupplierTitle,
    )

    if (hasMatchingSupplier) {
      setSupplierFilter(defaultSupplierTitle)
      setSearchQuery(defaultSupplierTitle)
    }
  }, [isOMSModalOpen, defaultSupplierTitle, allInvoices])

  useEffect(() => {
    const pendingInvoices = (uploadedInvoices || [])
      .filter(inv => inv.status === 'pending' || inv.status === undefined)
      .map(mapInvoiceToTableRow)

    setAllInvoices(pendingInvoices)
  }, [uploadedInvoices])

  const supplierOptions = useMemo(() => {
    const uniqueSuppliers = new Map()

    allInvoices.forEach(invoice => {
      if (!invoice.supplierTitle) {
        return
      }

      uniqueSuppliers.set(invoice.supplierTitle, {
        id: invoice.supplierTitle,
        title: invoice.supplierTitle,
      })
    })

    return Array.from(uniqueSuppliers.values()).sort((a, b) =>
      a.title.localeCompare(b.title),
    )
  }, [allInvoices])

  const dataSource = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()

    return allInvoices.filter(invoice => {
      const matchesSupplier =
        supplierFilter === 'all' || invoice.supplierTitle === supplierFilter

      if (!matchesSupplier) {
        return false
      }

      if (!query) {
        return true
      }

      return [
        invoice.supplierTitle,
        invoice.purchaseOrderNumber,
        invoice.fileName,
        invoice.invoiceNumber,
        invoice.notes,
      ].some(value =>
        String(value || '')
          .toLowerCase()
          .includes(query),
      )
    })
  }, [allInvoices, searchQuery, supplierFilter])

  const columns = [
    {
      title: 'Uploaded Date',
      dataIndex: 'uploadedDate',
      key: 'uploaded_date',
      render: dateTime => moment(dateTime).format('DD-MM-YYYY'),
    },
    {
      title: 'Uploaded By',
      dataIndex: 'username',
      key: 'uploaded_by',
    },
    {
      title: 'Supplier',
      dataIndex: 'supplierTitle',
      key: 'supplier_title',
    },
    {
      title: 'Invoice Number',
      dataIndex: 'invoiceNumber',
      key: 'invoice_number',
      render: value => value || '-',
    },
    {
      title: 'Name',
      dataIndex: 'fileName',
      key: 'file_name',
    },
    {
      title: 'PO Number',
      dataIndex: 'purchaseOrderNumber',
      key: 'purchaseOrderNumber',
      render: value => value || '-',
    },
    {
      title: 'Actions',
      dataIndex: 'invoiceUrl',
      key: 'invoiceUrl',
      render: (value, invoice) => (
        <a
          target='_blank'
          rel='noopener noreferrer'
          href={`/invoice/${venueId}/${invoice.id}`}
        >
          View
        </a>
      ),
    },
  ]

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys, rows) => {
      setSelectedRowKeys(keys)
      setSelectedRows(rows)
    },
  }

  const handleConfirm = () => {
    if (onInvoicesConfirmed) {
      onInvoicesConfirmed(selectedRows)
    } else {
      setSelectedInvoices(selectedRows)
    }
    setIsOMSModalOpen(false)
  }

  return (
    <Modal
      visible={isOMSModalOpen}
      onCancel={() => setIsOMSModalOpen(false)}
      footer={null}
      width={1200}
      closable={false}
      bodyStyle={{ padding: '0 20px' }}
    >
      <div style={{ padding: 24, borderBottom: '1px solid #f0f0f0' }}>
        <Button
          type='link'
          onClick={() => setIsOMSModalOpen(false)}
          style={{ padding: 0 }}
        >
          ←
        </Button>
        <span style={{ marginLeft: 8, fontWeight: 600, fontSize: 16 }}>
          Select Invoices from OMS Storage
        </span>
      </div>

      <div
        style={{
          margin: '20px 0 0',
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <Select
          showSearch
          allowClear
          placeholder='Filter by supplier'
          value={supplierFilter === 'all' ? undefined : supplierFilter}
          onChange={value => setSupplierFilter(value || 'all')}
          style={{ minWidth: 220 }}
          optionFilterProp='children'
        >
          {supplierOptions.map(supplier => (
            <Option key={supplier.id} value={supplier.id}>
              {supplier.title}
            </Option>
          ))}
        </Select>
        <Search
          allowClear
          placeholder='Search by supplier, invoice number, PO, or file name'
          value={searchQuery}
          onChange={event => setSearchQuery(event.target.value)}
          style={{ minWidth: 280, maxWidth: 480, flex: 1 }}
        />
      </div>

      <Table
        loading={uploadedLoading}
        style={{ margin: '20px 0' }}
        scroll={{ x: 800 }}
        rowKey='id'
        dataSource={dataSource}
        columns={columns}
        rowSelection={{ type: 'checkbox', ...rowSelection }}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          total: dataSource.length,
        }}
      />

      <div style={{ textAlign: 'right', marginTop: 16 }}>
        <Button
          onClick={() => setIsOMSModalOpen(false)}
          style={{ marginRight: 8 }}
        >
          Cancel
        </Button>
        <Button
          type='primary'
          onClick={handleConfirm}
          disabled={!selectedRows.length}
        >
          Confirm Selection ({selectedRows.length})
        </Button>
      </div>
    </Modal>
  )
}

export default SelectInvoicesModal
